import type {
  InsightsSavedView as BaseInsightsSavedView,
  TabType as BaseInsightsSavedViewTabType,
} from '@mend/types/insights-saved-view';
import type { Response } from '#types/common.ts';
import type { DatePresetKey } from '#utils/dates.ts';

import { z } from 'zod';

import { api } from '#services/http-clients.ts';
import { datePresets } from '#utils/dates.ts';
import { allProvidersToken } from '#utils/provider-filter.ts';

const datePresetKeys = Object.keys(datePresets) as [DatePresetKey, ...DatePresetKey[]];

const knownSavedViewFiltersSchema = z.object({
  datePreset: z.enum(datePresetKeys).optional(),
  from: z.string().date().optional(),
  to: z.string().date().optional(),
  providerIds: z.union([z.array(z.coerce.string()), z.literal(allProvidersToken)]).optional(),
  appointmentTypeIds: z.array(z.coerce.string()).optional(),
  addressIds: z.array(z.coerce.string()).optional(),
  bookingChannelIds: z.array(z.string()).optional(),
  baseAppointmentTypes: z.array(z.string()).optional(),
  assessmentTypeIds: z.array(z.coerce.string()).optional(),
  formsPriority: z.enum(['all', 'required', 'optional']).optional(),
  appointmentId: z.number().positive().optional(),
  appointmentStatusIds: z.array(z.coerce.string()).optional(),
  queueState: z.enum(['all', 'current', 'completed']).optional(),
  patientDisconnected: z.enum(['all', 'yes', 'no']).optional(),
});

const savedViewFiltersSchema = knownSavedViewFiltersSchema
  .passthrough()
  .refine((data) => !data.datePreset || (!data.from && !data.to), {
    message: 'datePreset and from/to are mutually exclusive',
  })
  .refine((data) => !(data.from && !data.to) && !(data.to && !data.from), {
    message: 'from and to must both be present if either is set',
  });

type SavedViewFilterValue =
  | string
  | number
  | boolean
  | null
  | SavedViewFilterValue[]
  | { [key: string]: SavedViewFilterValue };

export type SavedViewFilters = z.infer<typeof knownSavedViewFiltersSchema> &
  Record<string, SavedViewFilterValue | undefined>;

export type InsightsSavedViewTabType = BaseInsightsSavedViewTabType;

export interface InsightsSavedView extends Omit<BaseInsightsSavedView, 'filters' | 'tabType'> {
  tabType: InsightsSavedViewTabType;
  filters: SavedViewFilters;
}

export type SavedViewsListResponse = { views: InsightsSavedView[] };
type SavedViewResponse = { view: InsightsSavedView };
type BaseInsightsSavedViewResponse = Omit<BaseInsightsSavedView, 'tabType'> & {
  tabType: InsightsSavedViewTabType;
};

export const getSavedViews = (): Promise<SavedViewsListResponse> =>
  api
    .get<Response<{ views: BaseInsightsSavedViewResponse[] }>>('/insights/saved-views')
    .then((res) => ({
      views: res.data.payload.views.reduce<InsightsSavedView[]>((acc, view) => {
        const result = savedViewFiltersSchema.safeParse(view.filters);
        if (result.success) {
          // The response is JSON, while passthrough keeps API-owned keys opaque to this client.
          acc.push({ ...view, filters: result.data as SavedViewFilters });
        } else {
          console.error(`[saved-views] Malformed filters for view ${view.id}:`, result.error);
        }
        return acc;
      }, []),
    }));

export const createSavedView = (params: {
  tabType: InsightsSavedView['tabType'];
  name: string;
  filters: SavedViewFilters;
}): Promise<SavedViewResponse> =>
  api
    .post<Response<SavedViewResponse>>('/insights/saved-views', params)
    .then((res) => res.data.payload);

export const updateSavedView = (
  id: number,
  params:
    | { name: string; filters?: SavedViewFilters }
    | { name?: string; filters: SavedViewFilters }
): Promise<SavedViewResponse> =>
  api
    .put<Response<SavedViewResponse>>(`/insights/saved-views/${id}`, params)
    .then((res) => res.data.payload);

export const deleteSavedView = (id: number): Promise<Response> =>
  api.delete<Response>(`/insights/saved-views/${id}`).then((res) => res.data);
